import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Feature} from 'ol';
import { of, Observable } from 'rxjs';
import { GlobalSelectionControlService } from '../map/services/global-selection-control.service';
import { MenuManagementService } from '../menu/services/menu-management.service';
import { AirSpaceBoundaryModel } from '../models/air-space-boundary';
import { FeatureType } from '../models/EnumTypes';
import { ErrorModel } from '../models/error';
import { TrajectoryModel } from '../models/Trajectory';
import { DynamicTable } from '../popup/dynamic-table/dynamic-table.component';
import { SelectCoordinatePointsControlService } from '../popup/services/select-coordinate-points-control.service';
import { EndpointsService } from './endpoints.service';
import { MapService } from './map.service';
import { ModalConfirmationService } from './modal-confirmation.service';
import { ModalControlService } from './modal-control.service';
import { PointsService } from './points.service';
import { ProceduresService } from './procedures.service';
import { ProjectService } from './project.service';
import { SearchService } from './search.service';
import { ToolsControlService } from './tools-control.service';
import { UiBlockService } from './ui-block.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})

export class TrajectoryService {


  public active: boolean;
  public fullScreenMode:boolean = false;

  trajectories: TrajectoryModel[] = [];

  constructor(
    private projectService : ProjectService,

     private http: HttpClient,

     private endpointService : EndpointsService,
     private modalService : ModalControlService,
     private pointsService : PointsService,
     private modalConfirmation : ModalConfirmationService,
     private utilsService : UtilsService,
     private popupService : BsModalService,
     private uiBlock: UiBlockService
) { }

  updateMapStatus(trajectory: TrajectoryModel): void {
    if (trajectory.uniqueid != null) {
      this.http
        .patch(
          `${this.endpointService.apiUrlTrajectory}/${trajectory.uniqueid}/mapstatus`,
          trajectory,
          this.endpointService.httpOptions
        )
        .subscribe(
          (data : ErrorModel) => {
            if(data.code == 'OK'){
              this.modalService.showMesssageModal('Trajectory', 'Trajectory updated successfully.');
            }else {

              let message = data.message;
              this.modalService.showMesssageModal("Error", "Error updating trajectory." + message);
            }
            this.pointsService.deleteAirSpaceBoundariesFromMap();
            this.pointsService.fetchAndAddAirSpaceBoundaries();
            this.pointsService.clearSelectedFeatures();
          },
          (error) => {
            this.modalService.showMesssageModal("Error", "Error updating Trajectory map status.");
          }
        );
    }
    else {
      this.modalService.showMesssageModal('Trajectory', 'Error updating map status: invalid Trajectory ID');
    }
  }

  bulkUpdateTrajectoriesStatus(trajectoreis: TrajectoryModel[]): void {
    this.http
      .put(
        this.endpointService.apiUrlTrajectoryBulkStatusUpdate,
        trajectoreis,
        this.endpointService.httpOptions
      )
      .subscribe(
        (data : ErrorModel[]) => {
          this.pointsService.deleteAirSpaceBoundariesFromMap();
          this.pointsService.fetchAndAddAirSpaceBoundaries();
          this.pointsService.deleteTrajectoriesFromMap();
          this.pointsService.fetchAndAddTrajectories();
          this.pointsService.clearSelectedFeatures();
          if(data[0].code == 'OK'){
            this.modalService.showMesssageModal('Trajectories', 'Trajectories updated successfully.');
          }else {

            let message : string  = '';
            data.forEach( error => {
              if (message.length < 0){
                message = message + error.message + ';';
              }else {
                message = message + ' ' + error.message + ';';
              }
            });
            this.modalService.showMesssageModal("Error", "Error updating trajectories." + message);
          }
        },
        (error) => {
          this.modalService.showMesssageModal("Error", "Error updating trajectories.");
        }
      );
}

  deleteTrajectory(): void {
    if (!this.projectService.projectEditable()){
      return;
    }

    let features : Feature[] = this.pointsService.getSelectedFeatures().filter(feat => feat.get("featureType") == FeatureType.Trajectory);
    if(features.length == 0){
      this.modalService.showMesssageModal("Warning", "Select at least 1 Trajectory to delete.");
      return;
    }

    const bsModalRef : BsModalRef = this.popupService.show(DynamicTable, {
      backdrop: 'static' // Prevent closing on backdrop click
    });
    bsModalRef.content.entity = "Trajectory";
    bsModalRef.content.type = "Delete";
    bsModalRef.content.selectAllActive = true;
    bsModalRef.content.data = features;
    bsModalRef.content.displayedColumns = this.utilsService.getDynamicTableColumns("Trajectories");
    bsModalRef.content.onClose.subscribe(result => {
      if(result.length>0){
        let list : Feature[] = [];
        result.forEach(element => {
          list.push(this.pointsService.getSelectedFeatures().find(x => x.get("id") == element.get("id") && x.get("featureType") == FeatureType.Trajectory ));
        });
        this.modalConfirmation.showMesssageConfirmationModal("Delete Trajectory", "Do you want to delete the selected Trajectory?", "Yes", "No", () => this.deleteTrajectories(list, true));
      }
    });
  }

  deleteTrajectories(features : any, fromMapAndDatabase : boolean): void {
    this.uiBlock.startBlock("Deleting Trajectories...");
    let ids : number[] = [];

    for(let feature of features){
      ids.push(feature.get("uniqueid"));
    }
    this.pointsService.deleteTrajectoriesBulk(ids).subscribe(
      (result) =>{
        let source;
        features.forEach((feature) => {
          source = this.pointsService.trajecttoriesLayers.childLayers[0].layers
          .find((element) => element.id === 'Trajectory')
          .olLayer.getSource();

          source.removeFeature(feature);
          this.pointsService.removeSelectedFeature(feature);
        });
        this.modalService.showMesssageModal("Trajectories", "Trajectories deleted with success.");
        this.uiBlock.stopBlock();
      },
      (error) => {
        this.uiBlock.stopBlock();
        if (error && error.status == 401) {
          this.modalService.showMesssageModal(
            'Error Deleting',
            'Default Project is not editable.'
          )
          return;
        }
        this.modalService.showMesssageModal("Error Deleting", "Error deleting trajectories.");
      }
    )
  }

  deleteTrajectoryFromDb(id: string): Observable<boolean> {
    this.http
      .delete<boolean>(
        this.endpointService.apiUrlTrajectory + '/' + id,
        this.endpointService.httpOptions
      )
      .subscribe(
        () => {
          this.modalService.showMesssageModal("Delete Trajectories", "Trajectories deleted with success.");
          return true;
        },
        () => {
          this.modalService.showMesssageModal("Delete Trajectories", "Error deleting trajectories.");
          return false;
        }
      );
    return of(false);
  }

}
